export default function QRPilotSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - QRPilot
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for QRPilot.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I scan a QR code or barcode?
              </h3>
              <p className="mt-1">
                Open QRPilot and point your camera at the code. It is detected
                the moment it fills the frame — no shutter button needed. Turn on
                the built-in torch to scan in dim light.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Can I scan a code from a saved photo?
              </h3>
              <p className="mt-1">
                Yes. Tap the gallery option and pick an image that contains a QR
                code, and QRPilot will read it for you — useful for codes you
                received in a screenshot or a chat.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What are smart actions?
              </h3>
              <p className="mt-1">
                QRPilot recognizes what a code contains and offers the right next
                step — open a link, join a WiFi network, save a contact, call or
                message a number, send an email, open a location in maps, add a
                calendar event, or copy and share the value.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I create my own QR code?
              </h3>
              <p className="mt-1">
                Go to the generator, choose a type — website link, WiFi, contact
                card, text, email, SMS, phone, location, calendar event or social
                profile — and fill in the details. A live preview updates as you
                type, then you can save or share the finished code.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How does the WiFi QR code work?
              </h3>
              <p className="mt-1">
                Enter your network name and password, generate the code, and let
                guests join by scanning it — no need to read out or type the
                password.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Where are my scanned and created codes saved?
              </h3>
              <p className="mt-1">
                Everything you scan and create is added to your history
                automatically, and you can group codes into collections and pin
                the ones you use most. It all stays stored locally on your
                device.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Does the app work offline?
              </h3>
              <p className="mt-1">
                Yes. Scanning and generating codes works offline. Internet access
                is only required for services such as ads, analytics, or opening
                links you choose to act on.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What does the Pro upgrade include?
              </h3>
              <p className="mt-1">
                QRPilot is free to use. A single one-time Pro upgrade removes ads
                and unlocks unlimited history plus custom code colors and shapes.
                There is no subscription and no recurring charge.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why am I seeing ads?
              </h3>
              <p className="mt-1">
                Some versions of the app show ads to support ongoing development,
                maintenance, and feature updates. The one-time Pro upgrade
                removes them.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                The app is crashing — what should I do?
              </h3>
              <p className="mt-1">
                Make sure you are on the latest version from Google Play, then
                fully close and reopen the app. If the problem continues, email
                us the details below and we will help.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Support</h2>
          <p>
            If you need help, have feedback, or want to report an issue, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We typically respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            To help us resolve issues faster, include the following in your
            message when possible:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Your device model and OS version</li>
            <li>The app version shown in Settings</li>
            <li>A short description of the issue</li>
            <li>Steps to reproduce the problem</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">App Information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>App Name:</strong> QRPilot
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact Email:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
            <li>
              <strong>Privacy Policy:</strong>{" "}
              <a
                href="https://cosmovexapps.com/apps/qrpilot/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                https://cosmovexapps.com/apps/qrpilot/privacy-policy
              </a>
            </li>
            <li>
              <strong>Support URL:</strong>{" "}
              <a
                href="https://cosmovexapps.com/apps/qrpilot/support"
                className="text-[#0F6BBF] underline"
              >
                https://cosmovexapps.com/apps/qrpilot/support
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-center text-sm text-[#8A93A2]">
          © {new Date().getFullYear()} Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
